import React, { FC, useMemo } from 'react';

import { MarginProps } from '@opendoor/bricks/system/theme';
import { Box, Text, DropdownSingleSelect } from '@opendoor/bricks/core';

import { useHistory } from 'platform/HistoryProvider';
import { LateCheckoutForm } from 'sellDirect/components/closingDash/lateCheckout/LateCheckoutForm';
import {
  getSignersRows,
  WizardSummary,
  WizardHelloSignButtonsProps,
} from 'sellDirect/components/closingDash/WizardSummary';
import { useSupportContacts } from 'sellDirect/helpers/useSupportContacts';

import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { ClosedOffer, UnderContractOffer } from 'sellDirect/OfferContext';
import { getHubSellingRoute } from 'shared/helpers/routes';
import { FULL_DATE_AND_DAY_OF_WEEK_FORMAT } from 'shared/helpers/datetime';
import { ExternalLink } from 'platform/react-router';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

interface RequestLateCheckoutStepProps {
  /** Finalized offer */
  offer: UnderContractOffer | ClosedOffer;
  /** Current closing tasks */
  closingTasks: ClosingTasks;
  /** Function to advance to the next step in the wizard */
  goToSummaryForm: () => void;
}

interface RequestLateCheckoutStepSummaryProps
  extends Omit<RequestLateCheckoutStepProps, 'goToSummaryForm'> {
  /** Callback when user clicks Sign for a document and signatory pair */
  onClickSignButton: WizardHelloSignButtonsProps['onClickSignButton'];
  /** Function to advance to the previous step in the wizard */
  goToEditForm: () => void;
}

/**
 * First step in the Late Checkout flow
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const RequestLateCheckoutStep: FC<RequestLateCheckoutStepProps & MarginProps> = ({
  offer,
  closingTasks,
  goToSummaryForm,
  ...marginProps
}) => {
  const { addressUUID } = useQualificationContext();
  const { history, goBack } = useHistory();
  const initialLocation = useMemo(() => history.location, [history]);
  const occupancy = closingTasks.otherHomeInfo?.taskable.occupancyStatusAtAcquisition || undefined;

  return (
    <Box {...marginProps}>
      {offer.canScheduleLateCheckout && offer.state === 'UnderContract' ? (
        <LateCheckoutForm
          lateCheckoutTask={closingTasks.lateCheckout}
          occupancy={occupancy}
          offer={offer}
          onLateCheckoutRequested={() => goToSummaryForm()}
          onLateCheckoutCanceled={() => {
            goBack({
              before: initialLocation,
              fallbackLocation: getHubSellingRoute(),
              addressUUID,
            });
          }}
          onCanceled={() => goToSummaryForm()}
        />
      ) : (
        <CannotScheduleLateCheckout shouldSeeAgentExperience={offer.shouldSeeAgentExperience} />
      )}
    </Box>
  );
};

interface RequestLateCheckoutStepSummaryProps
  extends Omit<RequestLateCheckoutStepProps, 'goToSummaryForm'> {
  /** Callback when user clicks Sign for a document and signatory pair */
  onClickSignButton: WizardHelloSignButtonsProps['onClickSignButton'];
  /** Function to advance to the previous step in the wizard */
  goToEditForm: () => void;
}

/**
 * Second step in the Late Checkout flow
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const RequestLateCheckoutSummaryStep: FC<RequestLateCheckoutStepSummaryProps & MarginProps> =
  ({ offer, closingTasks, onClickSignButton, goToEditForm, ...marginProps }) => {
    const lateCheckoutDate = closingTasks.lateCheckout?.taskable.lateCheckoutDate;
    const occupancy =
      closingTasks.otherHomeInfo?.taskable.occupancyStatusAtAcquisition || undefined;
    return (
      <Box {...marginProps}>
        <WizardSummary
          offer={offer}
          rows={[
            ...getSignersRows(closingTasks),
            { title: 'Occupancy', value: occupancy || '-', trackingId: 'occupancy' },
            {
              title: 'Late checkout',
              value: lateCheckoutDate?.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT) || '-',
              trackingId: 'late-checkout',
            },
          ]}
          trackingName="late-checkout-summary"
          data-testid="late-checkout-summary"
          onEdit={offer.canScheduleLateCheckout ? () => goToEditForm() : undefined}
          disableEdit={offer.state === 'Closed'}
          helloSignTask={closingTasks.signLateCheckoutAddendum?.taskable}
          onClickSignButton={onClickSignButton}
        />
      </Box>
    );
  };

interface CannotScheduleLateCheckoutProps {
  shouldSeeAgentExperience: boolean;
}

/**
 * Content to show if the user cannot schedule a late checkout
 * component-test-check-ignore
 * storybook-check-ignore
 */
const CannotScheduleLateCheckout: FC<CannotScheduleLateCheckoutProps> = ({
  shouldSeeAgentExperience,
}) => {
  const { CXA_EMAIL } = useSupportContacts();

  return (
    <>
      <DropdownSingleSelect
        aria-labelledby="request-late-checkout"
        autoComplete="off"
        title="Request late checkout (Optional)"
        placeholder="Choose a date"
        onChange={() => {}}
        isDisabled
      />
      <Text mt="4">
        Need to change {shouldSeeAgentExperience ? 'the' : 'your'} the move-out date? Email{' '}
        <ExternalLink
          href={`mailto:${CXA_EMAIL}`}
          analyticsName="cake-lco-cxa-email"
          aria-label="Opens a new window to email Opendoor to change the move-out date"
        >
          {CXA_EMAIL}
        </ExternalLink>
        .
      </Text>
    </>
  );
};
